import "../css/header.scss";
import React, { useContext, useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { FiMenu, FiSearch, FiShoppingBag, FiX } from "react-icons/fi";
import { CartContext } from "./CartContext";

const categories = [
  "Jeans",
  "Shorts & Skirts",
  "Tops",
  "Dresses & Jumpsuits",
  "Jackets",
];

function Header() {
  const { itemCount } = useContext(CartContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const isHome = location.pathname === "/";

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.classList.toggle("menu-open", mobileOpen || searchOpen);
    return () => document.body.classList.remove("menu-open");
  }, [mobileOpen, searchOpen]);

  const submitSearch = (event) => {
    event.preventDefault();
    const value = query.trim();
    navigate(value ? `/shop?q=${encodeURIComponent(value)}` : "/shop");
  };

  return (
    <>
      <div className="announcement">Standard shipping is $9.99 per order.</div>
      <header
        className={`site-header ${isHome ? "site-header--overlay" : ""} ${
          mobileOpen || searchOpen ? "is-open" : ""
        }`}
      >
        <div className="site-header__inner">
          <button
            className="header-icon mobile-menu-trigger"
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <FiMenu aria-hidden="true" />
          </button>

          <nav className="desktop-nav" aria-label="Primary navigation">
            <div className="nav-shop">
              <NavLink to="/shop">SHOP</NavLink>
              <div className="mega-menu">
                <div>
                  <span className="mega-menu__label">Featured</span>
                  <Link to="/shop?sort=newest">New Arrivals</Link>
                  <Link to="/shop?category=Jeans">Signature Denim</Link>
                  <Link to="/shop?sale=true">Final Sale</Link>
                </div>
                <div>
                  <span className="mega-menu__label">Category</span>
                  {categories.map((category) => (
                    <Link
                      key={category}
                      to={`/shop?category=${encodeURIComponent(category)}`}
                    >
                      {category}
                    </Link>
                  ))}
                </div>
                <div>
                  <span className="mega-menu__label">Fit</span>
                  {["Slim", "Straight", "Wide & Barrel", "Bootcut & Flare"].map(
                    (fit) => (
                      <Link
                        key={fit}
                        to={`/shop?fit=${encodeURIComponent(fit)}`}
                      >
                        {fit}
                      </Link>
                    )
                  )}
                </div>
                <Link
                  className="mega-menu__editorial"
                  to="/shop?category=Jeans"
                >
                  <img src="/main/m-header-edit.png" alt="" />
                  <span>THE DENIM EDIT</span>
                </Link>
              </div>
            </div>
            <NavLink to="/about">ABOUT</NavLink>
            <NavLink to="/fit-guide">FIT GUIDE</NavLink>
          </nav>

          <Link to="/" className="wordmark" aria-label="nartvmora home">
            <img src="/brand-wordmark.svg" alt="nartvmora" />
          </Link>

          <div className="header-actions">
            <button
              className="header-icon"
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
            >
              <FiSearch aria-hidden="true" />
            </button>

            <Link
              className="header-icon cart-link"
              to="/cart"
              aria-label={`Shopping bag with ${itemCount} items`}
            >
              <FiShoppingBag aria-hidden="true" />
              {itemCount > 0 && <span>{itemCount}</span>}
            </Link>
          </div>
        </div>
      </header>

      <div
        className={`nav-drawer ${mobileOpen ? "is-visible" : ""}`}
        aria-hidden={!mobileOpen}
      >
        <div className="drawer-head">
          <span className="wordmark">
            <img src="/brand-wordmark.svg" alt="nartvmora" />
          </span>
          <button
            type="button"
            className="header-icon"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            <FiX />
          </button>
        </div>
        <nav aria-label="Mobile navigation">
          <Link to="/shop" className="drawer-feature">
            Shop all <span>→</span>
          </Link>
          <Link to="/shop?sort=newest">New arrivals</Link>
          {categories.map((category) => (
            <Link
              key={category}
              to={`/shop?category=${encodeURIComponent(category)}`}
            >
              {category}
              <span>→</span>
            </Link>
          ))}
          <Link to="/about">About</Link>
          <Link to="/fit-guide">Fit guide</Link>
          <Link to="/contact">Contact</Link>
          <Link to="/faqs">FAQs</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/cookies">Cookies</Link>
          <Link to="/terms">Terms</Link>
        </nav>
        <div className="drawer-foot">USD / Hong Kong</div>
      </div>
      {(mobileOpen || searchOpen) && (
        <button
          className="page-scrim"
          type="button"
          aria-label="Close overlay"
          onClick={() => {
            setMobileOpen(false);
            setSearchOpen(false);
          }}
        />
      )}

      <div
        className={`search-panel ${searchOpen ? "is-visible" : ""}`}
        aria-hidden={!searchOpen}
      >
        <div className="search-panel__head">
          <span className="wordmark">
            <img src="/brand-wordmark.svg" alt="nartvmora" />
          </span>
          <button
            type="button"
            className="header-icon"
            onClick={() => setSearchOpen(false)}
            aria-label="Close search"
          >
            <FiX />
          </button>
        </div>
        <form onSubmit={submitSearch} role="search">
          <label htmlFor="site-search">Search our collection</label>
          <div>
            <input
              id="site-search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search for anything"
              autoFocus={searchOpen}
            />
            <button type="submit" aria-label="Submit search">
              <FiSearch />
            </button>
          </div>
        </form>
        <p>
          Popular: <Link to="/shop?category=Jeans">Jeans</Link>{" "}
          <Link to="/shop?sort=newest">New arrivals</Link>
        </p>
      </div>
    </>
  );
}

export default Header;
